import { ContentType, Picture } from '@/@types/common'
import { Post } from '@/@types/post'
import Page from '@/components/Page'
import PictureGrid from '@/components/PictureGrid'
import PostList from '@/components/Post/PostList'
import { PostListContext } from '@/components/Post/PostListContext'
import TopicSubscriptionToggleButton from '@/components/TopicSubscriptionToggleButton'
import { getTopicDetailQueryOptions, getTopicFeedQueryOptions } from '@/utils/fetchers/topic'
import usePosts from '@/utils/hooks/usePosts'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { prepareToRefetchListQuery } from '@/utils/list'
import { assertTopicRoute } from '@/utils/navigate'
import queryClient from '@/utils/queryClient'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { Tabs } from '@ruguoapp/jike-fe-components'
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { MouseEvent, useCallback, useMemo } from 'react'
import { useTitle } from 'react-use'

export const Route = createFileRoute('/topic/$id/$tab')<unknown, { id: string; tab: string }>({
	component: TopicPage,
	loader: async ({ params }) => {
		const topic = await queryClient.ensureQueryData(getTopicDetailQueryOptions(params.id))
		assertTopicRoute(topic, params.tab)

		return {
			data: topic,
		}
	},
})

function TopicPage() {
	const { id, tab } = Route.useParams()
	const isHybridTab = tab === 'hybrid'
	const { data: topic } = useSuspenseQuery(getTopicDetailQueryOptions(id))
	const navigate = useUniversalNavigate()

	useTitle(`${topic.content} - 即刻`)
	return (
		<Page title="圈子详情">
			<div
				css={css`
					display: flex;
					max-width: 600px;
					padding: 16px;
					align-items: flex-end;
					gap: 12px;
				`}
			>
				<img
					width={150}
					height={150}
					src={topic.squarePicture.picUrl}
					css={css`
						width: 150px;
						height: 150px;
						flex-shrink: 0;
						border-radius: 12px;
						border: 1px solid var(--tint-pic-border, rgb(0 0 0 / 5%));
					`}
				/>
				<div
					css={css`
						display: grid;
						grid-template-rows: auto auto;
						gap: 11px;
						width: 100%;
					`}
				>
					<div
						css={css`
							display: flex;
							align-items: center;
							justify-content: space-between;
						`}
					>
						<div
							css={css`
								color: var(--tint-primary, #333);
								font-size: 26px;
								font-weight: 500;
								line-height: 32px;
							`}
						>
							{topic.content}
						</div>

						<TopicSubscriptionToggleButton id={id} />
					</div>
					<div
						css={css`
							color: var(--tint-secondary, #808080);
							font-family: 'Noto Sans SC';
							font-size: 18px;
							line-height: 24px;
						`}
					>
						{topic.subscribersDescription}
					</div>
				</div>
			</div>
			<Tabs
				onSelect={(value, e) => {
					navigate(
						{
							to: '/topic/$id/$tab',
							params: {
								id,
								tab: value,
							},
						},
						e
					)
				}}
				value={tab}
				data={topic.tabs.map((item) => ({
					label: item.name,
					value: item.type,
				}))}
				css={css`
					margin: 12px 0;

					.jk-tabs-item {
						flex: 1;
						color: var(--tint-primary, #333);
						text-align: center;
						font-family: 'Noto Sans SC';
						font-size: 18px;
						font-weight: 500;
						line-height: 26px;

						.jk-tabs-indicator {
							width: 100%;
						}
					}
				`}
			/>
			{isHybridTab ? <TopicHybridPage /> : <TopicPostsPage />}
		</Page>
	)
}

function TopicPostsPage() {
	const { id, tab } = Route.useParams()

	const { data, fetchNextPage, refetch, isFetching } = useSuspenseInfiniteQuery(
		getTopicFeedQueryOptions(id, tab)
	)
	const postIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const posts = usePosts(postIds)

	const refresh = useCallback(() => {
		prepareToRefetchListQuery(getTopicFeedQueryOptions(id, tab).queryKey)
		return refetch()
	}, [refetch, id, tab])

	return (
		<Page onReachPageBottom={fetchNextPage}>
			<PostListContext.Provider value={{ type: 'TOPIC' }}>
				<PostList data={posts} isFetching={isFetching} />
			</PostListContext.Provider>
		</Page>
	)
}

interface PostPicture extends Picture {
	post: Pick<Post, 'id' | 'user'>
}
function TopicHybridPage() {
	const { id, tab } = Route.useParams()
	const { data: topic } = useSuspenseQuery(getTopicDetailQueryOptions(id))
	const hybridURL = topic.tabs.find((item) => item.type === 'hybrid')?.url
	const topicId = hybridURL && new URL(hybridURL).searchParams.get('topic')

	const { data, fetchNextPage, refetch, isFetching } = useSuspenseInfiniteQuery(
		getTopicFeedQueryOptions(topicId, 'selected')
	)
	const postIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const posts = usePosts(postIds)
	const pictures = posts.reduce<PostPicture[]>((acc, post) => {
		if (post.type === ContentType.POST && post.pictures && post.user) {
			acc.push(
				...post.pictures.map<PostPicture>((item) => ({
					...item,
					post: pick(post, ['id', 'user']),
				}))
			)
		}
		return acc
	}, [])

	const navigate = useUniversalNavigate()
	const handleItemClick = (picture: PostPicture, index: number, e: MouseEvent) => {
		navigate(
			{
				to: '/u/$username/$type/$id',
				params: {
					username: picture.post.user!.username,
					type: POST_TYPE_TO_ROUTE_MAPPING[ContentType.POST],
					id: picture.post.id,
				},
			},
			e
		)
	}

	return (
		<Page onReachPageBottom={fetchNextPage}>
			<PictureGrid
				data={pictures}
				onItemClick={handleItemClick}
				css={css`
					justify-content: center;

					img {
						width: 140px;
						height: 140px;
					}
				`}
			/>
		</Page>
	)
}
